/* eslint-disable camelcase */
import { extractTextBetweenDoubleDashes, processArray } from '../../ms-teams-sdk/util/MsTeamsContextUtil';
import { setLocalStorageItem } from '../../packages/logger/src/LocalStorage';
import { Logger } from '../../packages/logger/src/Logger';
import { SD_ERROR_LIST_MSG, SD_FAILURE_NOTICATION, SD_NOTIFICATION, SD_SUCCESS_NOTICATION } from "../../packages/widgets/src/utils/MetricUtils";
import { ClientBuilderFactory } from '../remote/ClientBuilderFactory';
import { GraphURL } from '../util/URLUtils';
export interface IGraphExtension {
  '@odata.context': string;
  '@odata.type': string;
  id: string;
  extensionName: string;
  cisco_value: string;
}

export enum ExtensionIDs {
  CISCO_EXTENSION_FIRST = 'CISCO_EXTENSION_FIRST',
  CISCO_EXTENSION_SECOND = 'CISCO_EXTENSION_SECOND',
  // Remove later: earlier these extension were added so need to delete
  CISCO_EXTENSION_THIRD = 'CISCO_EXTENSION_THIRD',
  CISCO_EXTENSION_FOURTH = 'CISCO_EXTENSION_FOURTH'
}

/** Reverse map for encoded speed-dial rows: `{callType}--{contactId}--{phoneTypeAbbrev}` */
const PHONE_TYPE_ABBREV_TO_FULL: Record<string, string> = {
  wo: 'work',
  mo: 'mobile',
  ma: 'mail',
  ot: 'other',
  si: 'sip',
  ho: 'home',
  ex: 'extension',
};

const CALL_TYPE_ABBREV_TO_FULL: Record<string, string> = {
  ha: 'handset',
  vi: 'video',
};

/**
 * Parses legacy encoded strings such as `ha--{contactId}--ex` where the phone-type
 * abbreviation is not in the primary fromRaw loop (e.g. extension → ex).
 */
export function parseLegacyEncodedSpeedDial(rawValue: string) {
  if (!rawValue || typeof rawValue !== 'string') {
    return null;
  }

  const legacyPattern = /^[a-z]{2}--.+--[a-z]{2}$/i;
  if (!legacyPattern.test(rawValue)) {
    return null;
  }

  const contactId = extractTextBetweenDoubleDashes(rawValue);
  if (!contactId) {
    return null;
  }

  const callAbbrev = rawValue.substring(0, 2).toLowerCase();
  const typeAbbrev = rawValue.substring(rawValue.length - 2).toLowerCase();

  return {
    contactId,
    // Match legacy fromRaw default for unknown abbreviations; UUID extraction is the critical fix.
    phoneType: PHONE_TYPE_ABBREV_TO_FULL[typeAbbrev] ?? 'work',
    callType: CALL_TYPE_ABBREV_TO_FULL[callAbbrev] ?? 'handset',
  };
}

export async function init() {
    return Promise.all([
        // Remove later: earlier these extension were added so need to delete
        deleteIfExists(ExtensionIDs.CISCO_EXTENSION_THIRD),
        deleteIfExists(ExtensionIDs.CISCO_EXTENSION_FOURTH)]).then(function(values) {
        return Promise.all([
            createIfNotExists(ExtensionIDs.CISCO_EXTENSION_FIRST),
            createIfNotExists(ExtensionIDs.CISCO_EXTENSION_SECOND)]);
    });
}

export function needInit(error: any) {
    return error.response && error.response.status && error.response.status == 404;
}

async function getExtensionById(extensionId: string): Promise<IGraphExtension> {
    const graphClient = await ClientBuilderFactory.getGraphClient();
    const response = await graphClient.get(`${GraphURL.Extensions}/${extensionId}`);
    if (response.status === 200) {
        return response.data as IGraphExtension;
    } else {
        setLocalStorageItem(SD_ERROR_LIST_MSG, true);
        window.dispatchEvent(new Event("storage"));
        return Promise.reject(response);
    }
}

async function getAndInitExtentionById(extensionId: string): Promise<IGraphExtension> {
    return getExtensionById(extensionId).catch(function(error: any) {
		if (needInit(error)) {
			return init().then(function() {
				return getExtensionById(extensionId);
			});
		}
		return Promise.reject(error);
	});
}
// Check if org has admin consent (cached to avoid blocking Graph call on every load)
const ADMIN_CONSENT_CACHE_KEY = 'adminConsentGranted';
const ADMIN_CONSENT_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours

function getCachedAdminConsent(): boolean | null {
    try {
        const raw = localStorage.getItem(ADMIN_CONSENT_CACHE_KEY);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (cached?.granted && Date.now() - cached.ts < ADMIN_CONSENT_TTL_MS) {
            return true;
        }
        localStorage.removeItem(ADMIN_CONSENT_CACHE_KEY);
    } catch {
        localStorage.removeItem(ADMIN_CONSENT_CACHE_KEY);
    }
    return null;
}

function setCachedAdminConsent(): void {
    try {
        localStorage.setItem(ADMIN_CONSENT_CACHE_KEY, JSON.stringify({ granted: true, ts: Date.now() }));
    } catch {
        // storage full or unavailable — non-critical
    }
}

export function clearAdminConsentCache(): void {
    try {
        localStorage.removeItem(ADMIN_CONSENT_CACHE_KEY);
    } catch {
        // non-critical
    }
}

// Check if org has admin consent (cached to avoid blocking Graph call on every load)
export async function checkAdminConsent(): Promise<Boolean> {
    const cached = getCachedAdminConsent();
    if (cached === true) {
        return true;
    }

    const graphClient = await ClientBuilderFactory.getGraphClient();
    const response = await graphClient.get(`${GraphURL.Extensions}/${ExtensionIDs.CISCO_EXTENSION_FIRST}`).catch((error) => {
        return error?.response;
    });
    if (response?.response?.status === 403) {
        clearAdminConsentCache();
        return false;
    } else {
        setCachedAdminConsent();
        return true;
    } 
}

/**
 *
 */
export async function getExtensionFirst(): Promise<IGraphExtension> {
  return getAndInitExtentionById(ExtensionIDs.CISCO_EXTENSION_FIRST);
}

/**
 *
 */
export async function getExtensionSecond(): Promise<IGraphExtension> {
  return getAndInitExtentionById(ExtensionIDs.CISCO_EXTENSION_SECOND);
}

async function updateCiscoExtensionById(
  extensionId: string,
  cisco_value: string,
  notificationType: string
): Promise<void> {
  const graphClient = await ClientBuilderFactory.getGraphClient();
  
  // Ensure cisco_value is properly formatted as a clean JSON string
  // Parse and re-stringify to normalize any encoding issues
  let sanitisedCiscoValue: string;
  try {
    const parsedValue = JSON.parse(cisco_value);
    sanitisedCiscoValue = JSON.stringify(parsedValue);
  } catch (parseError) {
    Logger.error(`SD: Failed to parse cisco_value: ${JSON.stringify(parseError)}`);
    sanitisedCiscoValue = cisco_value;
  }
  
  const requestData = {
    cisco_value: sanitisedCiscoValue,
  };
  
  const response = await graphClient.patch(`${GraphURL.Extensions}/${extensionId}`, requestData);
  if (response.status === 204) {
        notificationSuccess(notificationType);
        return;
    } else {
        notificationFailure(notificationType);
        return Promise.reject(response);
    }
}

async function updateAndCiscoExtensionById(
    extensionId: string,
    cisco_value: string,
    notificationType: string
): Promise<void> {
    return updateCiscoExtensionById(extensionId, cisco_value, notificationType).catch(function (error) {
        if (needInit(error)) {
            return init().then(function () {
                return updateCiscoExtensionById(extensionId, cisco_value, notificationType);
            });
        }
        
        // Enhanced error logging for 400 errors to diagnose encoding issues
        if (error?.response?.status === 400 || error?.status === 400) {
            Logger.error(`SD: Extension update failed (400 Bad Request) - ${extensionId}: ${JSON.stringify(error?.response?.data)}`);
        }
        
        return Promise.reject(error);
    });
}

export async function updateCiscoExtensionFirst(
  cisco_value: string,
  notificationType: string
): Promise<void> {
  await updateAndCiscoExtensionById(
    ExtensionIDs.CISCO_EXTENSION_FIRST,
    cisco_value,
    notificationType
  );
}

export async function updateCiscoExtensionSecond(
  cisco_value: string,
  notificationType: string
): Promise<void> {
  await updateAndCiscoExtensionById(
    ExtensionIDs.CISCO_EXTENSION_SECOND,
    cisco_value,
    notificationType
  );
}

async function create(extensionId: string) {
    const graphClient = await ClientBuilderFactory.getGraphClient();
    const data = {
        "@odata.type": "microsoft.graph.openTypeExtension",
        extensionName: extensionId,
        "cisco_value": JSON.stringify([])
    };
    return await graphClient.post(`${GraphURL.Extensions}`, data).then(function (response) {
        return "";
    }).catch(function (error: any) {
        Logger.error(
            `MSTeamsExtensionService.create error: ${JSON.stringify(error)}`
          );
    })
}

async function exists(extensionId: string) {
    return getExtensionById(extensionId).then(function () {
        return true;
    }).catch(function (error: any) {
        if (error.response.status == 404) {
            return false;
        }
        return Promise.reject(error);
    });
}

async function createIfNotExists(extensionId: string) {
  return exists(extensionId).then(function(exists: any) {
      if (!exists) {
          return create(extensionId);
      }
  });
}

export function fromRaw(rawValue: any) {
    // Handle direct objects (new format) - objects are now stored directly in the array
    if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
        // Reducing noise - uncomment for debugging if needed
        // Logger.debug(`SD: MSTeamsExtensionService.fromRaw - Processing direct object format`);
        let parsedRawData = rawValue;
        // Reducing noise - uncomment for debugging if needed
        // console.log(`SD: MSTeamsExtensionService.fromRaw - Processing direct object format`, parsedRawData);
        // Apply the same processing logic as before
        if(!parsedRawData.phoneType) {
            const emailPattern = new RegExp(/[a-z\d._%+-]+@[a-z\d.-]+\.[a-z]{2,4}$/);
            if(parsedRawData.currentCallAddress && emailPattern.test(parsedRawData.currentCallAddress)) {
                parsedRawData.phoneType = "mail";
            } else {
                parsedRawData.phoneType = "work";
            }
            // Reducing noise - uncomment for debugging if needed
            // console.log(`SD: MSTeamsExtensionService.fromRaw - Processing direct object format`, parsedRawData.phoneType);
        }
        if(parsedRawData.displayName) {
            const names: string[] = parsedRawData.displayName.split(/\s+/);
            const isNameContainsComma = names?.some(element => element.startsWith(','));
            if(isNameContainsComma) {
                const [array1, array2] = processArray(names);
                parsedRawData.givenName = array1.join(' ');
                parsedRawData.surname = array2.join(' ');
                // Reducing noise - uncomment for debugging if needed
                // console.log(`SD: MSTeamsExtensionService.fromRaw - Processing direct object format`, parsedRawData.givenName, parsedRawData.surname);
            }
            else {
                if(parsedRawData.displayName.match(' ')) {
                    parsedRawData.givenName = parsedRawData.displayName.substring(0, parsedRawData.displayName.lastIndexOf(" ")).trim();
                    parsedRawData.surname = parsedRawData.displayName.split(' ').pop().trim();
                } else {
                    parsedRawData.givenName = parsedRawData.displayName;
                    parsedRawData.surname = '';
                }
                // Reducing noise - uncomment for debugging if needed
                // console.log(`SD: MSTeamsExtensionService.fromRaw - Processing direct object format`, parsedRawData.givenName, parsedRawData.surname);
            }
        }
        // Reducing noise - uncomment for debugging if needed
        // console.log(`SD: MSTeamsExtensionService.fromRaw - Processing direct object format`, parsedRawData);
        return parsedRawData;
    }

    // Handle legacy format - JSON strings (backward compatibility)
    if (!rawValue || typeof rawValue !== 'string') {
        Logger.info(`SD: MSTeamsExtensionService.fromRaw - Invalid rawValue type: ${typeof rawValue}, value: ${rawValue}`);
        return { 
            id: 'l_' + require('uuid').v4(), 
            displayName: 'Invalid Speed Dial', 
            phoneType: 'work', 
            callType: 'handset',
            currentCallAddress: '',
            isInvalid: true 
        };
    }

    // custom speed dial (legacy JSON string format)
    if (rawValue.startsWith("{")) {
        // Reducing noise - uncomment for debugging if needed
        // console.log(`SD: MSTeamsExtensionService.fromRaw - Processing legacy JSON string format`, rawValue);
        try {
            // Reducing noise - uncomment for debugging if needed
            // Logger.debug(`SD: MSTeamsExtensionService.fromRaw - Processing legacy JSON string format`);
            let parsedRawData = JSON.parse(rawValue);
            if(!parsedRawData.phoneType) {
            const emailPattern = new RegExp(/[a-z\d._%+-]+@[a-z\d.-]+\.[a-z]{2,4}$/);
            if(parsedRawData.currentCallAddress && emailPattern.test(parsedRawData.currentCallAddress)) {
                parsedRawData.phoneType = "mail";
            } else {
                parsedRawData.phoneType = "work";
            }
            // Reducing noise - uncomment for debugging if needed
            // console.log(`SD: MSTeamsExtensionService.fromRaw - Processing legacy JSON string format`, parsedRawData.phoneType);
        }
        if(parsedRawData.displayName) {
            const names: string[] = parsedRawData.displayName.split(/\s+/);
            const isNameContainsComma = names?.some(element => element.startsWith(','));
            if(isNameContainsComma) {
                const [array1, array2] = processArray(names);
                parsedRawData.givenName = array1.join(' ');
                parsedRawData.surname = array2.join(' ');
            }
            else {
                if(parsedRawData.displayName.match(' ')) {
                    parsedRawData.givenName = parsedRawData.displayName.substring(0, parsedRawData.displayName.lastIndexOf(" ")).trim();
                    parsedRawData.surname = parsedRawData.displayName.split(' ').pop().trim();
                } else {
                    parsedRawData.givenName = parsedRawData.displayName;
                    parsedRawData.surname = '';
                }
            }
        } 
        return parsedRawData;
        } catch (parseError) {
            Logger.error(`SD: MSTeamsExtensionService.fromRaw - Failed to parse JSON speed dial: ${JSON.stringify(parseError)}, rawValue: ${rawValue}`);
            // Return a safe fallback object for malformed JSON speed dials
            return { 
                id: 'l_' + require('uuid').v4(), 
                displayName: 'Invalid Speed Dial', 
                phoneType: 'work', 
                callType: 'handset',
                currentCallAddress: '',
                isInvalid: true 
            };
        }
    }

    // --mo stands for mobile, --wo stands for business phone, --ma stands for mail
    for (let type of ["work", "mobile", "mail", "other", "sip", "home"]) {
    // --ha stands for handset, --vi stands for video
        for (let callType of ["handset", "video"]) {
            if (rawValue.startsWith(callType.substring(0, 2) + "--") && rawValue.endsWith("--" + type.substring(0, 2))) {
                const id = extractTextBetweenDoubleDashes(rawValue);
                return { contactId: id, phoneType: type , callType: callType };
            }
        }
        if(rawValue.endsWith("--" + type.substring(0, 2))) {
            return { contactId: rawValue.substring(0, rawValue.length - 4), phoneType: type };
        }
    }

    const legacyEncoded = parseLegacyEncodedSpeedDial(rawValue);
    if (legacyEncoded) {
      return legacyEncoded;
    }

    // handle legacy data which doesn't have address type, use business phone by default.
    return { contactId: rawValue, phoneType: "work", callType: "handset" };
}

function sanitizeStoredText(value: unknown): string {
    if (typeof value !== 'string') {
        return '';
    }
    return value.replace(/[\u0000-\u001F\u007F-\u009F]/g, '').trim();
}

function isPlaceholderDisplayName(name?: string): boolean {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    return !trimmed || /^unknown( contact)?$/i.test(trimmed);
}

function hasRealStoredDisplay(value: any): boolean {
    const address = sanitizeStoredText(value?.currentCallAddress);
    const name = sanitizeStoredText(value?.displayName);
    return Boolean(address) || !isPlaceholderDisplayName(name);
}

function toCompactEncoding(value: any, contactId: string): string {
    return value.callType.substring(0, 2) + "--" + contactId + "--" + value.phoneType.substring(0, 2);
}

export function toRaw(value: any) {
    // { id: xxx, phoneType: "work" } => xxx--wo (except id starts with 'local_')
    // { displayName: xxx, primaryCallAddress: yyy} => { displayName: xxx, primaryCallAddress: yyy}
    value.phoneType = value.phoneType == 'Email' || value.phoneType == 'email'? 'mail' : value.phoneType;
    value.callType = value.callType === undefined ? value.callType = 'handset' : value.callType;
    
    if (value.contactId) {
        // Graph/Outlook contacts stay compact so a full list still fits the 2KB
        // extension chunks. Persist name/number only for DSS/Control Hub rows
        // that Graph and Webex People cannot rehydrate.
        if (value.fromDss && hasRealStoredDisplay(value)) {
            const minimalValue: any = {
                contactId: value.contactId,
                callType: value.callType,
                phoneType: value.phoneType,
                fromDss: true,
            };
            const displayName = sanitizeStoredText(value.displayName);
            if (displayName && !isPlaceholderDisplayName(displayName)) {
                minimalValue.displayName = displayName;
            }
            const currentCallAddress = sanitizeStoredText(value.currentCallAddress);
            if (currentCallAddress) {
                minimalValue.currentCallAddress = currentCallAddress;
            }
            return minimalValue;
        }
        return toCompactEncoding(value, value.contactId);
    } else {
        // For custom speed dials, return the clean object directly
        // JSON.stringify will be called at the array level, not individual objects
        const cleanValue = removeEmpty(value);
        // Reducing noise - uncomment for debugging if needed
        // console.log(`SD: MSTeamsExtensionService.toRaw - Processing direct object format`, cleanValue);
        // Sanitize displayName to prevent encoding issues
        if (cleanValue.displayName && typeof cleanValue.displayName === 'string') {
            // Remove any problematic characters that might cause JSON encoding issues
            cleanValue.displayName = cleanValue.displayName
                .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
                .trim(); // Remove leading/trailing whitespace
            // Reducing noise - uncomment for debugging if needed
            // console.log(`SD: MSTeamsExtensionService.toRaw - Processing direct object format`, cleanValue.displayName);
        }
        
        // Sanitize currentCallAddress
        if (cleanValue.currentCallAddress && typeof cleanValue.currentCallAddress === 'string') {
            cleanValue.currentCallAddress = cleanValue.currentCallAddress
                .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters  
                .trim(); // Remove leading/trailing whitespace
            // Reducing noise - uncomment for debugging if needed
            // console.log(`SD: MSTeamsExtensionService.toRaw - Processing direct object format`, cleanValue.currentCallAddress);
        }
        
        // Ensure all required fields are present with fallback values
        const safeCleanValue = {
            id: cleanValue.id || 'l_' + require('uuid').v4(),
            displayName: cleanValue.displayName || 'Unknown',
            callType: cleanValue.callType || 'handset',
            phoneType: cleanValue.phoneType || 'work',
            currentCallAddress: cleanValue.currentCallAddress || ''
        };
        
        // Reducing noise - uncomment for debugging if needed
        // Logger.debug(`SD: PROD FIX: toRaw returning clean object for speed dial: ${safeCleanValue.displayName}`);
        // console.log(`SD: MSTeamsExtensionService.toRaw - Processing direct object format`, safeCleanValue);
        return safeCleanValue;
    }
}

async function deleteIfExists(extensionId: string) {
  return exists(extensionId).then(function(exists) {
      if (exists) {
          return deleteExtension(extensionId);
      }
  });
}

async function deleteExtension(extensionId: string) {
  const graphClient = await ClientBuilderFactory.getGraphClient();
  const url = `${GraphURL.Extensions}/${extensionId}`;
  return await graphClient.delete(url);
}

function removeEmpty(obj: any) {
    const newObj:any = {};
    Object.keys(obj).forEach(function(k) {
        if (obj[k] && typeof obj[k] === "object") {
            const value = removeEmpty(obj[k]);
            if (Object.keys(value).length != 0) {
                newObj[k] = removeEmpty(obj[k]);
            }
        } else if (obj[k] != null && obj[k] != undefined) {
            newObj[k] = obj[k];
        }
    });
    const { id, displayName, callType, phoneType, currentCallAddress } = newObj;
    const extractedObj = { id, displayName, callType, phoneType, currentCallAddress };
    return extractedObj;
}

function notificationSuccess(notificationType: string) {
    switch(notificationType){
        case SD_NOTIFICATION.SD_ADDED:
        setLocalStorageItem(SD_SUCCESS_NOTICATION.SD_SUCCESS_ADD, true);
        window.dispatchEvent(new Event("storage"));
        break;
        case SD_NOTIFICATION.SD_DELETED:
        setLocalStorageItem(SD_SUCCESS_NOTICATION.SD_SUCCESS_DELETE, true);
        window.dispatchEvent(new Event("storage"));
        break;
        case SD_NOTIFICATION.SD_EDITED:
        setLocalStorageItem(SD_SUCCESS_NOTICATION.SD_SUCCESS_EDIT, true);
        window.dispatchEvent(new Event("storage"));
        break;
        case SD_NOTIFICATION.SD_REORDERED:
        setLocalStorageItem(SD_SUCCESS_NOTICATION.SD_SUCCESS_REORDER, true);
        window.dispatchEvent(new Event("storage"));
        break;
    }
}

    function notificationFailure(notificationType: string) {
        switch(notificationType){
            case SD_NOTIFICATION.SD_ADDED:
            setLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_ADD, true);
            window.dispatchEvent(new Event("storage"));
            break;
            case SD_NOTIFICATION.SD_DELETED:
            setLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_DELETE, true);
            window.dispatchEvent(new Event("storage"));
            break;
            case SD_NOTIFICATION.SD_EDITED:
            setLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_EDIT, true);
            window.dispatchEvent(new Event("storage"));
            break;
            case SD_NOTIFICATION.SD_REORDERED:
            setLocalStorageItem(SD_FAILURE_NOTICATION.SD_FAILURE_REORDER, true);
            window.dispatchEvent(new Event("storage"));
            break;
        }
}